// @ts-nocheck 
const u_GetCart = async () => {
  return await fetch("/cart.js").then(res => res.json());
}

const u_UpdateCartAttribute = async(obj) => {
  let data = { attributes: obj };

  return await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  }).then(res => res.json());
}

const u_StringToNode = (str) => {
  return new DOMParser().parseFromString(str, 'text/html');
}

const u_GetQueryParam = (key) => {
  if ('URLSearchParams' in window) {
    const url = new URL(window.location);
    return url.searchParams.get(key);
  }
}

// ============================================================================
// BB START: Estimated dates — Request Form + POD Variant Notes
// ----------------------------------------------------------------------------
// One set of rules and constants, used in two places:
//   • Request Form — the DISPATCH step and the customer's confirmation email
//     (both call u_RQF_EstimateDates, so they always agree).
//   • Product pages — POD Variant Notes show "Estimated Dispatch: Mon 12 Oct."
//     instead of a fixed print time (see PRODUCT PAGES further down).
//
// REQUEST FORM — every request starts with:
//   1. Approval — the Training Institute reviews the request ....... +RQF_APPROVAL_DAYS
//
// STOCK-ONLY requests: approval, payment and dispatch all fit within
//   RQF_STOCK_DISPATCH_DAYS working days of the request.
//
// Requests with ANY print-on-demand item (SKU contains '@p') continue:
//   2. Payment  — customer pays from the approval email ............ +RQF_PAYMENT_DAYS
//   3. Batch    — pod-sync sends orders to Ingram Mon & Thu at 6am Melbourne.
//                 An order paid during a batch day has missed that morning's
//                 batch, so we take the first batch day AFTER the payment day.
//                 (The batch runs even on public holidays; Ingram simply starts
//                 printing on the next working day.)
//   4. Print    — Ingram print time (Dash › Reports › POD Sync) ..... +RQF_POD_PRINT_DAYS
//                 (+RQF_POD_PRINT_DAYS_HARDCOVER if the request includes a POD hardcover)
//   5. To 3PL   — printed books reach the 3PL, usually p.m. ......... +RQF_POD_TO_3PL_DAYS
//   6. Dispatch — couriers collect ~noon, so an afternoon arrival
//                 ships the next working day ......................... +RQF_POD_COURIER_DAYS
//
// Requests with '@ls' items (and no '@p') — Ingram ships these straight to the
// customer, so after payment there's no batch wait and no 3PL:
//   2. Payment  — as above .......................................... +RQF_PAYMENT_DAYS
//   3. Dispatch — sent to Ingram within the hour, printed, then
//                 shipped by Ingram direct to the customer ........... + print days (as step 4)
// A request that mixes types gets the latest date: '@p', then '@ls', then stock.
//
// The estimate shows a single dispatch date: the first day it's expected to go out.
//
// "Working days" skip weekends and Victorian public holidays (Ingram prints in
// Melbourne and pod-sync runs on Melbourne time). Dates are worked out on the
// Melbourne calendar whatever the customer's own timezone, using whole-day UTC
// arithmetic so a daylight-saving change can never shift a date.
// ============================================================================

const RQF_APPROVAL_DAYS       = 1;      // request  → Institute approval
const RQF_PAYMENT_DAYS        = 1;      // approval → customer payment ('@p' and '@ls')
const RQF_STOCK_DISPATCH_DAYS = 2;      // stock-only: request → dispatch (approval + payment + dispatch)
const RQF_POD_BATCH_WEEKDAYS  = [1, 4]; // pod-sync batch days (0 = Sun … 6 = Sat): Mon, Thu
const RQF_POD_PRINT_DAYS      = 3;      // batch   → printed (Ingram), paperback
const RQF_POD_PRINT_DAYS_HARDCOVER = 6; // batch   → printed (Ingram), hardcover
const RQF_POD_TO_3PL_DAYS     = 1;      // printed → arrives at the 3PL
const RQF_POD_COURIER_DAYS    = 1;      // arrives at the 3PL → dispatched (misses the ~noon courier)
const RQF_TIMEZONE            = 'Australia/Melbourne';

// Victorian public holidays that fall on a WEEKDAY, from today onwards (weekend
// ones are already non-working, and Victoria adds no substitute day when ANZAC
// Day falls on a weekend).
// ⚠ UPDATE EACH YEAR — https://business.vic.gov.au/business-information/public-holidays
const RQF_VIC_PUBLIC_HOLIDAYS = new Set([
  '2026-11-03', // Melbourne Cup Day
  '2026-12-25', // Christmas Day
  '2026-12-28', // Boxing Day (additional day — 26 Dec is a Saturday)
  '2027-01-01', // New Year's Day
  '2027-01-26', // Australia Day
  '2027-03-08', // Labour Day
  '2027-03-26', // Good Friday
  '2027-03-29', // Easter Monday
  '2027-06-14', // King's Birthday
  '2027-09-24', // Friday before the AFL Grand Final — expected date, confirm once announced
  '2027-11-02', // Melbourne Cup Day
  '2027-12-27', // Christmas Day (additional day — 25 Dec is a Saturday)
  '2027-12-28', // Boxing Day (additional day — 26 Dec is a Sunday)
]);

const RQF_DAY_NAMES   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const RQF_MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The Melbourne calendar date (as a Date at 00:00 UTC) and the Melbourne hour
// (0–23) for an instant. Only the calendar date matters for counting days, so
// everything below uses the UTC getters/setters on these dates.
const u_RQF_MelbourneNow = (instant = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: RQF_TIMEZONE, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23'
  }).formatToParts(instant);
  const part = (type) => Number(parts.find(p => p.type === type).value);
  return { date: new Date(Date.UTC(part('year'), part('month') - 1, part('day'))), hour: part('hour') };
};

// Today's date on the Melbourne calendar.
const u_RQF_MelbourneToday = () => u_RQF_MelbourneNow().date;

const u_RQF_AddDays = (date, days) => {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
};

// A working day is a weekday that isn't a Victorian public holiday.
const u_RQF_IsWorkingDay = (date) => {
  const weekday = date.getUTCDay();
  const isoDate = date.toISOString().slice(0, 10); // 'YYYY-MM-DD'
  return weekday !== 0 && weekday !== 6 && !RQF_VIC_PUBLIC_HOLIDAYS.has(isoDate);
};

// Step forward `days` working days. The starting day itself is never counted,
// so +1 working day from a Friday or a Saturday is the following Monday.
const u_RQF_AddWorkingDays = (date, days) => {
  let d = new Date(date);
  while (days > 0) {
    d = u_RQF_AddDays(d, 1);
    if (u_RQF_IsWorkingDay(d)) days--;
  }
  return d;
};

// First pod-sync batch day strictly AFTER the given day.
const u_RQF_NextPodBatchAfter = (date) => {
  let d = u_RQF_AddDays(date, 1);
  while (!RQF_POD_BATCH_WEEKDAYS.includes(d.getUTCDay())) d = u_RQF_AddDays(d, 1);
  return d;
};

// 'Mon 5 Oct'
const u_RQF_FormatDay = (date) =>
  `${RQF_DAY_NAMES[date.getUTCDay()]} ${date.getUTCDate()} ${RQF_MONTH_NAMES[date.getUTCMonth()]}`;

// Estimated approval date and dispatch date for a request made `today`.
//   route     — '@p' if any item is print-on-demand via the 3PL, else '@ls' if any item
//               ships direct from Ingram, else 'stock' (RequestForm.u_PodRoute works this out)
//   printDays — Ingram print time: RQF_POD_PRINT_DAYS, or RQF_POD_PRINT_DAYS_HARDCOVER if the
//               request includes a POD hardcover (RequestForm.u_PodPrintDays works this out)
//   today     — only passed in by tests; normally the current Melbourne date
// Returns display strings: { approval: 'Mon 5 Oct', dispatch: 'Thu 15 Oct' }
const u_RQF_EstimateDates = (route, printDays = RQF_POD_PRINT_DAYS, today = u_RQF_MelbourneToday()) => {
  const approval = u_RQF_AddWorkingDays(today, RQF_APPROVAL_DAYS);

  let dispatch;
  if (route === '@p') {
    const paid    = u_RQF_AddWorkingDays(approval, RQF_PAYMENT_DAYS);
    const batch   = u_RQF_NextPodBatchAfter(paid);
    const printed = u_RQF_AddWorkingDays(batch, printDays);
    const at3pl   = u_RQF_AddWorkingDays(printed, RQF_POD_TO_3PL_DAYS);
    dispatch      = u_RQF_AddWorkingDays(at3pl, RQF_POD_COURIER_DAYS);
  } else if (route === '@ls') {
    // sent to Ingram within the hour of payment; Ingram prints and ships direct to the customer
    const paid = u_RQF_AddWorkingDays(approval, RQF_PAYMENT_DAYS);
    dispatch   = u_RQF_AddWorkingDays(paid, printDays);
  } else {
    dispatch = u_RQF_AddWorkingDays(today, RQF_STOCK_DISPATCH_DAYS);
  }

  return {
    approval: u_RQF_FormatDay(approval),
    dispatch: u_RQF_FormatDay(dispatch),
  };
};

// ----------------------------------------------------------------------------
// PRODUCT PAGES — POD Variant Notes
// ----------------------------------------------------------------------------
// A shop order is paid at checkout, so there's no approval or payment wait:
//   batch → print (by format) → +RQF_POD_TO_3PL_DAYS → +RQF_POD_COURIER_DAYS
// pod-sync picks up new orders at :15 past each hour and sends the batch at 6am,
// so an order placed before BB_POD_BATCH_CUTOFF_HOUR on a Mon/Thu (Melbourne time)
// still makes that morning's batch; otherwise it waits for the next batch day.
// '@ls' items skip both the batch and the 3PL — sent to Ingram within the hour and
// shipped by Ingram direct to the customer — so they're simply order date + print days.
const BB_POD_BATCH_CUTOFF_HOUR = 5;

// Variant Notes that quote a print time (Shopify metaobject type 'note', by
// handle) and Ingram's print time for that format, in working days.
const BB_POD_NOTE_PRINT_DAYS = {
  'pod-paperback':        RQF_POD_PRINT_DAYS,           // POD Paperback
  'subsidised-pod':       RQF_POD_PRINT_DAYS,           // POD Subsidised (all paperbacks) — keeps its "Subsidised…" line
  'pod-hardcover':        RQF_POD_PRINT_DAYS_HARDCOVER, // POD Hardcover
  'pod-hardcover-colour': RQF_POD_PRINT_DAYS_HARDCOVER, // POD Hardcover Colour
};

// The print-time wording in those notes, e.g. "5-7 Working Days to Print" or
// "8–11 working days to print". Only this phrase is swapped for the estimate.
const BB_POD_PRINT_TEXT = /\d+\s*[-–]\s*\d+\s+working\s+days\s+to\s+print/i;

// First estimated dispatch day for an '@p' item (Mon/Thu batch, then the 3PL)
// ordered at `instant` (default: now), e.g. 'Mon 12 Oct'. `printDays` comes from
// BB_POD_NOTE_PRINT_DAYS.
const u_BB_PodDispatchFromNow = (printDays, instant = new Date()) => {
  const now = u_RQF_MelbourneNow(instant);
  const batchToday = RQF_POD_BATCH_WEEKDAYS.includes(now.date.getUTCDay()) && now.hour < BB_POD_BATCH_CUTOFF_HOUR;
  const batch = batchToday ? now.date : u_RQF_NextPodBatchAfter(now.date);
  const dispatch = u_RQF_AddWorkingDays(batch, printDays + RQF_POD_TO_3PL_DAYS + RQF_POD_COURIER_DAYS);
  return u_RQF_FormatDay(dispatch);
};

// First estimated dispatch day for an '@ls' item (no batch wait; Ingram ships it
// direct to the customer) ordered at `instant` (default: now), e.g. 'Wed 7 Oct'.
const u_BB_LsDispatchFromNow = (printDays, instant = new Date()) =>
  u_RQF_FormatDay(u_RQF_AddWorkingDays(u_RQF_MelbourneNow(instant).date, printDays));

// In every POD Variant Note inside `root`, replace the print-time phrase with
// "Estimated Dispatch: Mon 12 Oct." — any other lines in the note stay as they are.
// The note's own text is still in the page HTML, so it remains the fallback if
// this script doesn't run. Runs on page load (below) and again each time the
// customer switches variant, because <variant-note> then swaps in fresh HTML
// (see _bb-product-variants.js).
const u_BB_FillPodDispatchNotes = (root = document) => {
  root.querySelectorAll('[data-bb-note]:not([data-bb-filled])').forEach(($note) => {
    const printDays = BB_POD_NOTE_PRINT_DAYS[$note.dataset.bbNote];
    if (!printDays) return; // not a print-on-demand note
    $note.dataset.bbFilled = '1';
    // '@ls' ships direct from Ingram; '@p' goes via the Mon/Thu batch and the 3PL (from the variant's SKU)
    const shipsDirect = ($note.dataset.bbSku || '').includes('@ls');
    const date = shipsDirect ? u_BB_LsDispatchFromNow(printDays) : u_BB_PodDispatchFromNow(printDays);
    const estimate = `Estimated Dispatch: ${date}.`;
    let replaced = false;
    const walker = document.createTreeWalker($note, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (BB_POD_PRINT_TEXT.test(node.nodeValue)) {
        node.nodeValue = node.nodeValue.replace(BB_POD_PRINT_TEXT, estimate);
        replaced = true;
      }
    }
    if (!replaced) {
      console.warn(`[BB] Variant Note "${$note.dataset.bbNote}" has no "x–y working days to print" phrase to replace — showing its own text.`);
    }
  });
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => u_BB_FillPodDispatchNotes());
} else {
  u_BB_FillPodDispatchNotes();
}

// Console reminder for whoever maintains the holiday list: once we reach the
// last year it covers, ask for next year's dates (estimates near year-end would
// otherwise miss early-January holidays).
(() => {
  const lastYear = Math.max(...[...RQF_VIC_PUBLIC_HOLIDAYS].map(d => Number(d.slice(0, 4))));
  if (u_RQF_MelbourneToday().getUTCFullYear() >= lastYear) {
    console.warn(`[RQF] RQF_VIC_PUBLIC_HOLIDAYS ends in ${lastYear} — please add next year's Victorian public holidays (_bb-custom.js).`);
  }
})();
// BB END: Estimated dates — Request Form + POD Variant Notes
// ============================================================================

document.addEventListener('alpine:init', () => {
  // BB START: bridge URL configurable via theme setting (window.BB_BRIDGE_URL), falls back to hardcoded default if unset
  const REQUEST_FORM_API_URL = window.BB_BRIDGE_URL || 'https://app.shopify.bds.bahai.org.au/api';
  // BB END: configurable bridge URL
  const BRIDGE_KEY = 'fd89d6ad4fe04320a2d0201eeda49490c2725d7b7e2f94ff';

  Alpine.data('ButtonRequestFormInitiate', () => ({
    async e_InitRequestForm() {
      // Get the request form data from the dataset, save it to localStorage
      const request_form_id = this.$el.dataset.request_form_id;
      localStorage.setItem('bb_request_form_id', request_form_id);
      localStorage.setItem('bb_request_language', this.$el.dataset.language);
      localStorage.setItem('bb_request_return_product_url', this.$el.dataset.return_product_url);

      // open the link after saving to localStorage
      if (this.$el.target == '_blank') {
        window.open([this.$el.getAttribute('href'), '_blank']);
      }
      else {
        window.location.href = this.$el.getAttribute('href');
      }      
    }
  }));

  let DEBUG_MODE = false || u_GetQueryParam('debug') !== null;
  let RQF_id = localStorage.getItem('bb_request_form_id') || u_GetQueryParam('form') || '';
  let RQF_data = REQUEST_FORMS.GetFormData(RQF_id);
  let RQF_language = localStorage.getItem('bb_request_language');
  // BB START: fix — only default to English when arriving via navigation menu (query param);
  // when arriving from a product page button, respect the language stored by that button click
  if (u_GetQueryParam('form') !== null) {
    RQF_language = 'English';
  }
  // BB END: fix language default
  if (RQF_data) {
    RQF_data.language = RQF_language;
    RQF_data.return_product_url = localStorage.getItem('bb_request_return_product_url') || '/';
  }

  Alpine.data('RequestFormLoader', () => ({
    loaded: false,
    RQF_data: RQF_data,
    async init() {
      let cart = await u_GetCart();

      // Set cart attribute to capture rqf data to use variables in liquid
      let attributes = cart.attributes;
      attributes = {
        ...attributes,
        'rqf_menu': this.RQF_data.menu,
        'rqf_cluster_menu': this.RQF_data.cluster,
        'rqf_language': this.RQF_data.language
      };
      await u_UpdateCartAttribute(attributes);

      // Fetch the form from section
      let api_section_id = 'bb-api-section-request-form';
      let res = await fetch(`/?sections=${api_section_id}&d=${new Date()}`).then(res => res.json());
      let res_section = res[api_section_id];
      // console.log(res_section)

      // append the form to the page
      this.$el.querySelector('[data-rqf-form]').innerHTML = res_section;

      // Prefer wrapping at "(" in product titles
      this.$el.querySelectorAll('.bb-modal-trigger').forEach(btn => {
        btn.innerHTML = btn.innerHTML.replace(/ \(([^)]+)\)/g, '\u00A0<wbr><span style="white-space:nowrap">($1)</span>');
      });

      // Apply title color scheme
      this.$el.querySelectorAll('.h5').forEach(el => {
        el.classList.add(`color-${this.$el.dataset.title_color_scheme}`);
      });

      this.loaded = true;
    }
  }));

  Alpine.data('RequestForm', () => ({
    DEBUG_MODE: DEBUG_MODE, // TODO - remove after testing
    API_URL: REQUEST_FORM_API_URL,
    RQF_data: RQF_data,
    languages: [],
    selected: {
      step: 1,
      category: '',
      language: RQF_data.language,
      paying: RQF_data.customer_pays_text,
      postage: RQF_data.shipping1_text,
      delivery_date: '',
      estimated_approval: '', // BB: shown on the DISPATCH step
      estimated_dispatch: '',
      order_tags: [],
      show_digital: false,
      shipping_address: {
        email: '',
        first_name: '',
        last_name: '',
        address1: '',
        address2: '',
        city: '',
        province: '',
        country: '',
        zip: '',
        phone: '',
        note: '',
        cluster: '',
        cluster_id: ''
      },
      products: []
    },
    flags: {
      step_1_valid: false,
      step_2_valid: false,
      step_3_valid: true,
      step_4_valid: false,
      processing: false,
      request_submitted: false,
      // BB: drive the "Note required when Institute pays" UI feedback
      institute_pays: false,
      note_needs_detail: false
    },
    messages: {
      invalid_step_1: 'Please select at least one product to proceed.',
      invalid_step_2: 'Please fill in all required fields to proceed.',
      invalid_step_3: 'Please review your information before submitting.',
      invalid_step_4: 'Please agree to the terms and conditions to proceed.'
    },

    init() {
      this.InitWatchers();
      this.InitSelf();
    },

    InitSelf() {
      // BB START: stop qty-selector events from bubbling to cart-items-component.
      // cart-items-component listens at document level for 'quantity-selector:update'
      // and triggers a cart API update. That update sets data-cart-quantity on the
      // input, causing getEffectiveMax() = max - cartQuantity = 0, clamping the input
      // to 0 whenever x-show hides a language section and blur fires setQuantity().
      this.$el.addEventListener('quantity-selector:update', e => e.stopPropagation());
      // BB END: stop qty-selector event propagation

      this.selected.category = this.$el.dataset.initial_tab;
      this.$nextTick(this.UpdateTabs.bind(this));
      this.$nextTick(this.UpdateLanguages.bind(this));

      let shipping_address = this.$el.dataset.customer_shipping_address ? JSON.parse(this.$el.dataset.customer_shipping_address) : null;
      let customer_email = this.$el.dataset.customer_email || '';
      this.selected.shipping_address = {
        email: customer_email,
        first_name: shipping_address ? shipping_address.first_name : '',
        last_name: shipping_address ? shipping_address.last_name : '',
        address1: shipping_address ? shipping_address.address1 : '',
        address2: shipping_address ? shipping_address.address2 : '',
        city: shipping_address ? shipping_address.city : '',
        province: shipping_address ? shipping_address.province : '',
        country: shipping_address ? shipping_address.country : 'Australia',
        zip: shipping_address ? shipping_address.zip : '',
        phone: shipping_address ? shipping_address.phone : '',
        note: '',
        cluster: '',
        cluster_id: ''
      }

      if (this.DEBUG_MODE) {
        this.selected.shipping_address = {
          email: 'sv.vsat2k1@gmail.com', first_name: 'SV', last_name: 'SV', address1: 'Foo bar street 123', address2: '', city: 'Foobar', province: 'Australian Capital Territory', country: 'Australia', zip: '12345', phone: '5141234567', note: 'Note', cluster: 'Brisbane Bayside', cluster_id: 'NEA C12 QLD'
        };
      };


      let $title = document.querySelector('#request-form-title');
      if ($title) {
        $title.textContent = this.RQF_data.request_form_title || 'Request Form';
      }
    },

    InitWatchers() {
      this.$watch('selected.language', this.UpdateTabs.bind(this));
      // BB START: update language list when category (L1 tab) changes
      this.$watch('selected.category', this.UpdateLanguages.bind(this));
      // BB END: update language list when category changes
      this.$watch('selected', this.CheckFlags.bind(this));
      this.$watch('selected.step', () => {
        this.$nextTick(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          // Reset sticky-header state so the header re-appears after the scroll.
          // Without this, data-sticky-state can remain 'idle' (opacity:0) when the
          // page height shrinks between steps and the scroll position resets to 0.
          const $header = document.querySelector('#header-component');
          if ($header) $header.removeAttribute('data-sticky-state');
        });
      });
      this.$watch('selected.products', this.UpdateEstimatedDispatch.bind(this));
      this.$watch('selected.shipping_address.cluster', (val) => {
        let $option = this.$el.querySelector(`select[name='note_attributes[cluster]'] option[value="${val}"]`);
        if ($option) {
          this.selected.shipping_address.cluster_id = $option.dataset.cluster_id || '';
        }
      });
    },

    UpdateTabs() {
      let $tabs = this.$el.querySelectorAll('.rqf-tabs-nav__item');
      $tabs.forEach($tab => {
        let category = $tab.dataset.category;
        let $rq_category = this.$el.querySelector(`request-category[data-category="${category}"]`);
        let $rq_language = $rq_category ? $rq_category.querySelector(`request-language[data-language="${this.selected.language}"]`) : null;
        // console.log($tab, category, $rq_category, $rq_language);
        $tab.style.display = $rq_language ? 'block' : 'none';
      });
    },

    UpdateLanguages() {
      // BB START: scope language list to active category (L1 tab) to match menu order
      // and update when switching tabs. Previously collected from entire form DOM
      // (wrong order, never updated on tab change).
      let $cat = this.$el.querySelector(`request-category[data-category="${this.selected.category}"]`);
      let $langEls = $cat ? $cat.querySelectorAll('[data-language]') : [];
      let languages = [...new Set(Array.from($langEls).map(el => el.dataset.language))];
      this.languages = languages;
      // if the current language isn't available in this category, reset to first available
      if (!languages.includes(this.selected.language)) {
        this.selected.language = languages[0] || '';
      }
      // BB END: scope language list to active category
    },

    CheckFlags() {
      this.flags.step_1_valid = this.selected.products.length > 0;
      // BB START: when Institute is paying, the Note (intended use) is mandatory and must be meaningful
      // (>= 20 chars and >= 4 words). note_needs_detail drives the inline feedback + red field state so
      // the user understands why the form will not advance.
      let institutePays = !!(this.selected.paying && this.selected.paying.indexOf('Institute') >= 0);
      let note = (this.selected.shipping_address.note || '').trim();
      let noteMeaningful = note.length >= 20 && note.split(/\s+/).filter(Boolean).length >= 4;
      this.flags.institute_pays = institutePays;
      this.flags.note_needs_detail = institutePays && !noteMeaningful;
      // BB END
      this.flags.step_2_valid = this.selected.shipping_address.first_name.trim() != '' &&
        this.selected.shipping_address.last_name.trim() != '' &&
        this.selected.shipping_address.address1.trim() != '' &&
        this.selected.shipping_address.city.trim() != '' &&
        this.selected.shipping_address.country.trim() != '' &&
        this.selected.shipping_address.province.trim() != '' &&
        this.selected.shipping_address.zip.trim() != '' &&
        this.selected.shipping_address.cluster.trim() != '' &&
        this.selected.shipping_address.email.trim() != '' &&
        !this.flags.note_needs_detail; // BB: block advance until a meaningful Note is given when Institute pays
      // console.log({flags: this.flags});
    },

    // BB START: estimated approval + dispatch — the rules and every number live in the
    // "Estimated dates — Request Form + POD Variant Notes" block at the top of this file.
    UpdateEstimatedDispatch() {
      let estimate = u_RQF_EstimateDates(this.u_PodRoute(), this.u_PodPrintDays());
      this.selected.estimated_approval = estimate.approval;
      this.selected.estimated_dispatch = estimate.dispatch;
    },

    // How this request will be fulfilled, from its SKUs: '@p' if any item is print-on-demand
    // via the 3PL, else '@ls' if any item ships direct from Ingram, else 'stock'.
    // '@p' wins because it always dispatches last.
    u_PodRoute() {
      const skus = this.selected.products.map(p => (p.variant && p.variant.sku) || '');
      if (skus.some(sku => sku.includes('@p'))) return '@p';
      if (skus.some(sku => sku.includes('@ls'))) return '@ls';
      return 'stock';
    },

    // Print time for this request: a POD hardcover takes longer to print, and the slowest
    // item sets the pace. Format comes from the variant's Medium (data-medium on the product
    // row), falling back to the variant name or a '-H' SKU. Stock items don't affect it.
    u_PodPrintDays() {
      const isPodHardcover = (p) => {
        const sku = (p.variant && p.variant.sku) || '';
        if (!sku.includes('@p') && !sku.includes('@ls')) return false;
        return (p.medium || '').includes('hardcover')
          || ((p.variant && p.variant.title) || '').toLowerCase().includes('hardcover')
          || /-H(@|$)/.test(sku);
      };
      return this.selected.products.some(isPodHardcover) ? RQF_POD_PRINT_DAYS_HARDCOVER : RQF_POD_PRINT_DAYS;
    },
    // BB END: estimated approval + dispatch

    e_UpdateProductList(e) {
      // console.group('Product LIST');
      let product = JSON.parse(e.currentTarget.dataset.product);
      let $qty = e.currentTarget.querySelector('[name="quantity"]');
      // BB START: read qty directly — bound to quantity-selector:update which fires
      // synchronously after increaseQuantity/decreaseQuantity/setQuantity has already
      // written the new value to the input. No deferral needed.
      // Previously used @mouseup + $nextTick/$setTimeout, but mouseup fires before
      // click (where increaseQuantity runs), so the old value was always read.
      let selected_products = [...this.selected.products];
      let qty = $qty ? parseInt($qty.value) : 0;
      // BB END: direct read on quantity-selector:update
      $qty.classList.toggle('quantity-selected', qty > 0);
      $qty.closest('.quantity-selector').classList.toggle('quantity-selected', qty > 0);
      if (qty == 0) {
        selected_products = selected_products.filter(p => p.id != product.id);
      }
      else {
        let existing_product = selected_products.find(p => p.id == product.id);
        if (existing_product) {
          existing_product.quantity = qty;
        }
        else {
          selected_products.push({
            id: product.id,
            title: product.title,
            handle: product.handle,
            variant: product.variants[0],
            medium: e.currentTarget.dataset.medium || '', // BB: format handle, e.g. 'hardcover' (sets print days)
            quantity: qty
          });
        }
      }
      selected_products = selected_products.filter(p => p.quantity > 0);
      this.selected.products = [...selected_products];
      // console.groupEnd();
    },

    CheckInputs(e) {
      let $input = e.target;
      $input.classList.remove('input-error');
      let $label = this.$el.querySelector(`label[for="${$input.id}"]`);
      if ($label) {
        $label.classList.remove('label-error');
      }
    },

    ShowErrorInputs() {
      let $form_step = this.$el.closest('.form-wrap');
      if (!$form_step) return;
      let $inputs = $form_step.querySelectorAll('[aria-required="true"]');
      $inputs.forEach($input => {
        let $label = $form_step.querySelector(`label[for="${$input.id}"]`);
        if ($input.getAttribute('aria-invalid') == 'true') {
          $input.classList.add('input-error');
          if ($label) {
            $label.classList.add('label-error');
          }
        }
        else {
          $input.classList.remove('input-error');
          if ($label) {
            $label.classList.remove('label-error');
          }
        }

        // check email
        if ($input.type == 'email') {
          let email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!email_regex.test($input.value.trim())) {
            $input.classList.add('input-error');
            if ($label) {
              $label.classList.add('label-error');
            }
          }
        }
      });

      // scroll to the first invalid input
      let $first_invalid_input = $form_step.querySelector('[aria-required="true"][aria-invalid="true"]');
      if ($first_invalid_input) {
        $first_invalid_input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    e_ClearSelectedProducts(e) {
      this.selected.products = [];
      let $req_form = e.currentTarget.closest('request-form');
      $req_form.querySelectorAll('[name="quantity"]').forEach($qty => {
        $qty.value = 0;
      });
    },

    async e_SubmitRequest() {
      this.flags.processing = true;

      // Determine who is paying
      let institute_pays = this.selected.paying.indexOf('Institute') >= 0;

      // Find or create the Shopify customer (one bridge call; handles search,
      // create, and the phone-already-taken edge case server-side).
      let res_customer;
      if (institute_pays) {
        // Use the Training Institute account (search by its email).
        res_customer = await fetch(`${REQUEST_FORM_API_URL}/find-or-create-customer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-BB-Bridge-Key': BRIDGE_KEY },
          body: JSON.stringify({ email: this.RQF_data.account_email })
        }).then(res => res.json()).catch(err => { console.error('[RQF] customer lookup error:', err); return null; });
      } else {
        // Use the person as the Shopify customer.
        res_customer = await fetch(`${REQUEST_FORM_API_URL}/find-or-create-customer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-BB-Bridge-Key': BRIDGE_KEY },
          body: JSON.stringify({
            email: this.selected.shipping_address.email,
            first_name: this.selected.shipping_address.first_name,
            last_name: this.selected.shipping_address.last_name,
            verified_email: false,
            accepts_marketing: true,
            phone: this.selected.shipping_address.phone,
            addresses: [{
              address1: this.selected.shipping_address.address1,
              last_name: this.selected.shipping_address.last_name,
              first_name:  this.selected.shipping_address.first_name
            }]
          })
        }).then(res => res.json()).catch(err => { console.error('[RQF] customer lookup error:', err); return null; });
      }
      // Bridge contract: { ok, customers:[...] } on success, { ok:false, message } on failure.
      if (!res_customer || res_customer.ok === false || !res_customer.customers || res_customer.customers.length === 0) {
        console.error('[RQF] customer step failed:', res_customer);
        alert((res_customer && res_customer.message) || 'We could not set up your details. Please try again or contact us.');
        this.flags.processing = false;
        return;
      }

      // Create draft order
      let customer = res_customer.customers[0];

      let order_tags = [
        this.RQF_data.institute,
        this.selected.shipping_address.cluster,
        this.selected.shipping_address.cluster_id,
        institute_pays ? 'Institute Pays' : 'Customer Pays'
      ];

      let draft_order = {
        customer: {
          id: customer.id,
          phone: this.selected.shipping_address.phone,
        },
        email: this.selected.shipping_address.email,
        line_items: this.selected.products.map(p => ({
          variant_id: p.variant.id,
          quantity: p.quantity
        })),
        note_attributes: [
          { name: 'Person Ordering', value: this.selected.shipping_address.email },
          { name: 'Cluster - Neighborhood', value: this.selected.shipping_address.cluster },
          { name: 'Payment Request', value: this.selected.paying },
          { name: 'Postage Option', value: this.selected.postage },
          { name: 'Required By', value: this.selected.delivery_date },
        ],
        shipping_address: {
          first_name: this.selected.shipping_address.first_name,
          last_name: this.selected.shipping_address.last_name,
          address1: this.selected.shipping_address.address1,
          address2: this.selected.shipping_address.address2,
          city: this.selected.shipping_address.city,
          province: this.selected.shipping_address.province,
          country: this.selected.shipping_address.country,
          zip: this.selected.shipping_address.zip,
          phone: this.selected.shipping_address.phone,
        },
        // BB START: set billing_address = shipping_address — prevents Shopify from backfilling
        // the Institute customer's saved address (which cycles through previous recipients)
        billing_address: {
          first_name: this.selected.shipping_address.first_name,
          last_name:  this.selected.shipping_address.last_name,
          address1:   this.selected.shipping_address.address1,
          address2:   this.selected.shipping_address.address2,
          city:       this.selected.shipping_address.city,
          province:   this.selected.shipping_address.province,
          country:    this.selected.shipping_address.country,
          zip:        this.selected.shipping_address.zip,
          phone:      this.selected.shipping_address.phone,
        },
        // BB END: set billing_address = shipping_address
        tags: order_tags.join(','),
        note: this.selected.shipping_address.note,
      };

      // BB START: capture HTTP errors alongside network errors so API rejections are visible
      let res_draft_order = await fetch(`${REQUEST_FORM_API_URL}/draft-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-BB-Bridge-Key': BRIDGE_KEY
        },
        body: JSON.stringify({
          draft_order
        })
      })
      .then(res => res.json())
      .catch(err => {
        console.error('[RQF] draft-order network error:', err);
        return null;
      });
      // BB START: gate debug log behind DEBUG_MODE — draft-order response contains customer data
      if (DEBUG_MODE) console.log('[RQF] draft-order response:', res_draft_order);
      // BB END: gate debug log
      // BB END: draft-order error visibility

      // BB START: only show success if Shopify returned a draft_order object
      if (res_draft_order && res_draft_order.draft_order) {
        // BB START: clear rqf cart attributes after successful submission so they don't bleed onto subsequent regular orders
        try {
          let cart = await u_GetCart();
          await u_UpdateCartAttribute({
            ...cart.attributes,
            'rqf_menu': '',
            'rqf_cluster_menu': '',
            'rqf_language': ''
          });
        } catch(e) {
          console.warn('[RQF] Could not clear rqf cart attributes:', e);
        }
        // BB END: clear rqf cart attributes
        setTimeout(async () => {
          this.selected.step = 5;
          this.flags.processing = false;
          this.flags.request_submitted = true;
          await this.EmailtoAdmin(res_draft_order);
          // BB: also email the customer a confirmation (reply-to the managing institute)
          await this.EmailToCustomer(res_draft_order);
        }, 1000);
      } else {
        console.error('[RQF] draft-order failed:', res_draft_order);
        alert((res_draft_order && res_draft_order.message) || 'We could not complete your request. Please try again or contact us.');
        this.selected.step = 5;
        this.flags.processing = false;
        this.flags.request_submitted = false;
      }
      // BB END: success guard
    },

    async EmailtoAdmin(res_draft_order, digitalVariantIds = new Set()) {
      let order = res_draft_order.draft_order;
      let first_name = this.selected.shipping_address.first_name;
      let last_name = this.selected.shipping_address.last_name;
      let data = {
        to: this.RQF_data.account_email,
        subject: `Request Form order from ${first_name} ${last_name}`,
        html: `Dear Administrator,<br/><br/>You have a Request Form order from the Baha'i Books Website<br/><br/>
- Customer: ${first_name} ${last_name}<br/>
- Email: ${this.selected.shipping_address.email}<br/>
- Phone: ${this.selected.shipping_address.phone}<br/>
<br/>
Shipping Address: ${this.selected.shipping_address.address1}, ${this.selected.shipping_address.address2}, ${this.selected.shipping_address.city}, ${this.selected.shipping_address.province}, ${this.selected.shipping_address.country}, ${this.selected.shipping_address.zip}<br/>
<br/>
- Cluster: ${this.selected.shipping_address.cluster} (${this.selected.shipping_address.cluster_id})<br/>
- Payment: ${this.selected.paying}<br/>
- Postage Option: ${this.selected.postage}<br/>
- Required By: ${this.selected.delivery_date}<br/>
<br/>
- Order Details: ${order.line_items.map(item => `${item.quantity} x ${item.title}`).join(', ')}<br/>
<br/>
- Draft Order Link: https://${Shopify.shop}/admin/draft_orders/${order.id}`
      };

      await fetch(`${REQUEST_FORM_API_URL}/send-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-BB-Bridge-Key': BRIDGE_KEY
        },
        body: JSON.stringify({
          data: data
        })
      })
      .then(res => res.json())
      .catch(err => {
        console.error(err);
      });
    },

    // BB START: send the customer a confirmation email. From = Baha'i Books system
    // address (SPF/DKIM); Reply-To = the managing Training Institute so replies reach
    // its Ruhi Administrator. Best-effort — never blocks the order (mirrors EmailtoAdmin).
    async EmailToCustomer(res_draft_order) {
      let order = res_draft_order.draft_order;
      let first_name = this.selected.shipping_address.first_name;
      let items = order.line_items.map(item => `${item.quantity} × ${item.title}`).join(', ');
      // BB: only include the Notes line when the customer entered a note
      let noteText = (this.selected.shipping_address.note || '').trim();
      let notesLine = noteText ? `\n<li>Notes: ${noteText}</li>` : '';
      // BB: approval/dispatch estimate as at the moment of submission (same rules as the form)
      let estimate = u_RQF_EstimateDates(this.u_PodRoute(), this.u_PodPrintDays());
      let data = {
        to: this.selected.shipping_address.email,
        replyTo: this.RQF_data.account_email,
        subject: `Your Baha'i Books request has been received`,
        html: `Dear ${first_name},<br/><br/>
Your request for Institute materials has been received at ${this.RQF_data.institute}.<br/><br/>
Replies to this email go directly to the Institute's Ruhi Administrator. Please allow two days for the processing of your request. If you are covering the cost, you'll receive a separate email for payment once your request is authorised.<br/><br/>
<strong>Your request</strong>
<ul>
<li>Materials: ${items}</li>
<li>Cluster: ${this.selected.shipping_address.cluster}</li>
<li>Payment: ${this.selected.paying}</li>
<li>Postage: ${this.selected.postage}</li>
<li>Estimated Request Approval: ${estimate.approval}</li>
<li>Estimated Dispatch: ${estimate.dispatch}</li>${notesLine}
</ul>
<br/>
With warm greetings,<br/>
Baha'i Books Australia`
      };
      await fetch(`${REQUEST_FORM_API_URL}/send-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-BB-Bridge-Key': BRIDGE_KEY
        },
        body: JSON.stringify({ data: data })
      })
      .then(res => res.json())
      .catch(err => {
        console.error('[RQF] customer confirmation email error:', err);
      });
    },
    // BB END: customer confirmation email

    e_OpenEmailForm() {
      document.dispatchEvent(new CustomEvent('open:form'));
    }
  }));

  Alpine.data('SubmitWholesaleOrder', () => ({
    button_text: 'Submit Wholesale Order',
    admin_email: '',
    flags: {
      processing: false,
      submitted: false
    },
    init() {
      this.button_text = this.$el.dataset.button_text || 'Submit Wholesale Order';
      this.admin_email = this.$el.dataset.contact_email || 'bds@bahai.org.au';
      this.InitWatchers();
    },
    InitWatchers() {
      this.$watch('flags.processing', (val) => {
        this.button_text = val ? 'Processing' : (this.$el.dataset.button_text || 'Submit Wholesale Order');
      });
    },
    // BB START: wholesale order success — right-panel overlay matching cart drawer position
    async Success(res_draft_order) {
      let success_message = this.$el.dataset.success_message;
      let contact_name = this.$el.dataset.contact_name;
      let contact_email = this.$el.dataset.contact_email;

      // Close the cart drawer so the reactive re-render from ClearCart doesn't interfere
      let $dialog = document.querySelector('.cart-drawer__dialog');
      if ($dialog && $dialog.close) $dialog.close();

      // BB START: own backdrop — the dialog's ::backdrop vanishes on close, so add one to keep the overlay colour
      let $backdrop = document.getElementById('wholesale-order-backdrop');
      if (!$backdrop) {
        $backdrop = document.createElement('div');
        $backdrop.id = 'wholesale-order-backdrop';
        $backdrop.className = 'wholesale-order-success-backdrop';
        document.body.appendChild($backdrop);
      }
      $backdrop.classList.remove('hidden');
      // BB END: own backdrop

      // Show confirmation in a fixed right-panel that occupies the same space as the cart drawer
      let $container = document.getElementById('wholesale-order-confirmation');
      if (!$container) {
        $container = document.createElement('div');
        $container.id = 'wholesale-order-confirmation';
        $container.className = 'wholesale-order-success-message-container';
        document.body.appendChild($container);
      }
      $container.innerHTML = `
        <div class="wholesale-order-success-message">
          ${success_message}
          <p>Draft order ${res_draft_order.draft_order.name} received by ${contact_name}.</p>
          <p>Shipping and discounts to be finalized and communicated from ${contact_email}.</p>
          <p>Finalised order to be authorized by the customer.</p>
        </div>
      `;
      $container.classList.remove('hidden');

      // Dismiss panel (and backdrop) when clicking outside the panel
      setTimeout(() => {
        const dismiss = (e) => {
          if (!$container.contains(e.target)) {
            $container.classList.add('hidden');
            $backdrop.classList.add('hidden'); // BB: hide backdrop with the panel
            document.removeEventListener('click', dismiss);
          }
        };
        document.addEventListener('click', dismiss);
      }, 0);

      await this.ClearCart();
    },
    // BB END: wholesale order success
    async ClearCart() {
      await fetch('/cart/clear.js');
      document.querySelectorAll('.cart-bubble').forEach($bubble => {
        $bubble.classList.add('hidden');
      });
      document.querySelectorAll('.header-actions__cart-icon.header-actions__cart-icon--has-cart').forEach($item => {
        $item.classList.remove('header-actions__cart-icon--has-cart');
      });
    },
    async e_SubmitWholesaleOrder() {
      // BB START: guard customer/shipping_address before processing starts — prevents the
      // button getting stuck on "Processing" if the account has no readable shipping address
      let customer = window.customer;
      if (!customer || !customer.shipping_address) {
        alert('We could not read your account shipping address. Please check your account details or contact us.');
        return;
      }
      // BB END: customer guard

      this.flags.processing = true;

      // BB START: wrap submit body in try/catch so any unexpected error resets the button and informs the user
      try {
        let cart = await fetch(`/cart.js?ts=${Date.now()}`).then(res => res.json());

        // collect digital item variant IDs from cart DOM (metafield flag set in Liquid)
        let digitalVariantIds = new Set();
        document.querySelectorAll('.cart-item[data-is-digital="true"]').forEach(function(row) {
          digitalVariantIds.add(row.dataset.key.split(':')[0]);
        });

        cart.note = cart.note + `\nOrder Weight: ${this.$el.dataset.total_weight} kg`;

        let order_tags = ['Wholesale', `Wholesale ${this.$el.dataset.wholesale_title}`];

        let draft_order = {
          customer: {
            id: customer.id
          },
          email: customer.email,
          line_items: cart.items.map(item => ({
            variant_id: item.id,
            quantity: item.quantity
          })),
          note_attributes: [],
          shipping_address: {
            first_name: customer.shipping_address.first_name,
            last_name: customer.shipping_address.last_name,
            address1: customer.shipping_address.address1,
            address2: customer.shipping_address.address2,
            city: customer.shipping_address.city,
            province: customer.shipping_address.province,
            country: customer.shipping_address.country,
            zip: customer.shipping_address.zip,
            phone: customer.shipping_address.phone,
          },
          tags: order_tags.join(','),
          note: cart.note,
        };

        let res_draft_order = await fetch(`${REQUEST_FORM_API_URL}/draft-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-BB-Bridge-Key': BRIDGE_KEY
          },
          body: JSON.stringify({
            draft_order
          })
        })
        .then(res => res.json())
        .catch(err => {
          console.error(err);
        });

        setTimeout(async() => {
          this.flags.processing = false;

          // BB: only treat as success if the bridge actually created the draft order.
          if (res_draft_order && res_draft_order.draft_order) {
            this.flags.submitted = true;
            this.EmailtoAdmin(res_draft_order, digitalVariantIds); // fire and forget
            await this.Success(res_draft_order);
          } else {
            console.error('[Wholesale] draft-order failed:', res_draft_order);
            alert((res_draft_order && res_draft_order.message) || 'We could not place your wholesale order. Please try again or contact us.');
          }
        }, 1000);
      } catch (err) {
        console.error('[Wholesale] submit error:', err);
        this.flags.processing = false;
        alert('Something went wrong placing your wholesale order. Please try again or contact us.');
      }
      // BB END: try/catch wrapper
    },
    async EmailtoAdmin(res_draft_order, digitalVariantIds = new Set()) {
      let order = res_draft_order.draft_order;
      let to_email = this.admin_email;
      let addr = order.shipping_address;
      let addrParts = [addr.address1, addr.address2, addr.city, addr.province, addr.country, addr.zip].filter(Boolean).join(', ');
      let html = 'Hi Administrator,<br/><br/>You have a new wholesale order from Baha\'i Books Website<br/><br/>'
        + 'Customer: ' + order.customer.first_name + ' ' + order.customer.last_name + '<br/>'
        + 'Email: ' + order.customer.email + '<br/>'
        + (order.customer.phone ? 'Phone: ' + order.customer.phone + '<br/>' : '')
        + '<br />'
        + 'Shipping Address: ' + addrParts + '<br />'
        + '<br />'
        + 'Order Details:<br/>' + order.line_items.map(function(item) {
          var line = item.quantity + ' x ' + item.title;
          if (digitalVariantIds.has(String(item.variant_id))) {
            return '<small><span style="color:#cc0000">' + line + ' — Digital: not for wholesale</span></small>';
          }
          return '<small>' + line + '</small>';
        }).join('<br/>')
        + '<br /><br />'
        + 'Draft Order Link: https://' + Shopify.shop + '/admin/draft_orders/' + order.id;
      let data = {
        to: to_email,
        subject: "Baha'i Books Wholesale Order",
        html: html
      };

      let res_email = await fetch(`${REQUEST_FORM_API_URL}/send-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-BB-Bridge-Key': BRIDGE_KEY
        },
        body: JSON.stringify({
          data: data
        })
      })
      .then(res => res.json())
      .catch(err => {
        console.error(err);
      });
    }
  }));

  Alpine.data('EmailFormDialog', () => ({
    form_data: {
      name: '',
      email: '',
      message: ''
    },
    flags: {
      processing: false,
      submitted: false
    },
    init() {
      this.InitWatchers();
    },
    InitWatchers() {
      document.addEventListener('open:form', (e) => {
        this.$el.querySelector('.header-actions__action').click();
      });
    },

    async e_SubmitForm(e) {
      if (this.form_data.name.trim() == '' || this.form_data.email.trim() == '' || this.form_data.message.trim() == '') {
        alert('Please fill in all fields before submitting the form.');
        return;
      }

      let to_email = e.currentTarget.dataset.accountEmail;
      let data = {
        to: to_email,
        subject: "Enquiry to Ruhi Book Administrator from Baha’i Books website",
        // BB START: replace hardcoded debug name/email with actual form_data values
        html: `<meta charset="utf-8">\n<p><span>Dear Book Administrator,</span><br><br></p>\n<p>From the Baha'i Books website Institute Request Form, <span>${ this.form_data.name } has an inquiry...</span><br><br><strong>Contact:</strong><br><span>${ this.form_data.name }<br>${ this.form_data.email }</span><br><br><strong>Message:</strong><br><span>${ this.form_data.message }</span></p>`
        // BB END: form_data values
      };

      this.flags.processing = true;

      let res_email = await fetch(`${REQUEST_FORM_API_URL}/send-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-BB-Bridge-Key': BRIDGE_KEY
        },
        body: JSON.stringify({
          data: data
        })
      })
      .then(res => res.json())
      .then(res => {
        this.flags.submitted = true;

        // reset form after 15 seconds
        setTimeout(this.ResetForm.bind(this), 15000);
      })
      .catch(err => {
        console.error(err);        
      });
    },

    ResetForm() {
      this.form_data = {...{
        name: '',
        email: '',
        message: ''
      }};

      this.flags.processing = false;
      this.flags.submitted = false;
    }
  }));
});